import { useRef, useCallback } from 'react';
import { useContactMutations } from './use-contact-mutations';
import { ContactPropertiesInput } from '../graphql/types-and-hooks';

interface PendingUpdate {
  contactId: string;
  properties: Partial<ContactPropertiesInput>;
  customProperties?: Record<string, any>;
  timeoutId: NodeJS.Timeout;
}

// Exported for the field-history lookup (src/lib/field-history/field-key.ts),
// which must ask for the exact property this hook writes.
export const COLUMN_TO_PROPERTY_MAP: Record<string, keyof ContactPropertiesInput> = {
  name: 'firstName', // Special case - will split into firstName/lastName
  firstName: 'firstName',
  lastName: 'lastName',
  title: 'jobTitle',
  phone: 'phone',
  location: 'city', // Special case - will parse into city/state
  linkedin: 'linkedinProfileUrl',
  lastStep: 'lastActivityText',
  nextStep: 'nextActivityText',
};

export const useDebouncedContactUpdate = (debounceMs: number = 0) => {
  const { updateContact } = useContactMutations();
  const pendingUpdatesRef = useRef<Map<string, PendingUpdate>>(new Map());

  const scheduleUpdate = useCallback(
    (contactId: string, columnKey: string, value: string) => {
      // Skip fields that require special handling
      if (columnKey === 'email') {
        console.warn(`Field ${columnKey} requires special handling (primary email update)`);
        return;
      }
      // Note: company field is handled separately via EditableCompany component

      // Cancel existing timeout for this contact
      const existing = pendingUpdatesRef.current.get(contactId);
      if (existing?.timeoutId) {
        clearTimeout(existing.timeoutId);
      }

      // Determine if this is a standard property or custom property
      const isCustomColumn = columnKey.startsWith('custom_');

      let properties: Partial<ContactPropertiesInput> = existing?.properties || {};
      let customProperties: Record<string, any> = existing?.customProperties || {};

      if (isCustomColumn) {
        // Custom property
        const customKey = columnKey.replace('custom_', '');

        // For numeric/currency fields, convert string to number if it's a valid number
        // The backend stores these with value_numeric metadata
        // For checkbox fields, empty string means unchecked (don't convert to number)
        let fieldValue: any = value;
        if (value && value !== "" && !isNaN(Number(value))) {
          // Check if this looks like a numeric value (no letters, only digits, dots, minus)
          if (/^-?\d*\.?\d+$/.test(value.trim())) {
            fieldValue = Number(value);
          }
        }

        customProperties = {
          ...customProperties,
          [customKey]: fieldValue,
        };
      } else if (columnKey === 'name') {
        // Special case: name column needs to be split into firstName/lastName
        const [firstName, ...lastNameParts] = value.split(' ').filter(Boolean);
        properties = {
          ...properties,
          firstName: firstName || '',
          lastName: lastNameParts.join(' ') || '',
        };
      } else if (columnKey === 'location') {
        // Special case: parse "City, State, Country" format
        const locationParts = value.split(',').map(s => s.trim());
        properties = {
          ...properties,
          city: locationParts[0] || '',
          state: locationParts[1] || '',
          country: locationParts[2] || '',
        };
      } else {
        // Standard property
        const propertyKey = COLUMN_TO_PROPERTY_MAP[columnKey];
        if (propertyKey) {
          properties = {
            ...properties,
            [propertyKey]: value,
          };
        }
      }

      // Schedule the update (immediate if debounceMs = 0)
      const timeoutId = setTimeout(async () => {
        const update = pendingUpdatesRef.current.get(contactId);
        if (!update) return;

        try {
          await updateContact({
            id: contactId,
            properties: properties as ContactPropertiesInput, // Always send properties (even if empty) - matches coffee-ui pattern
            customProperties: Object.keys(customProperties).length > 0 ? customProperties : undefined,
          });

          // Clear from pending after successful update
          pendingUpdatesRef.current.delete(contactId);
        } catch (error) {
          console.error('Failed to update contact:', error);
          // Keep in pending so user can retry or see it failed
        }
      }, debounceMs);

      // Store pending update
      pendingUpdatesRef.current.set(contactId, {
        contactId,
        properties,
        customProperties,
        timeoutId,
      });
    },
    [updateContact, debounceMs]
  );

  const flushPendingUpdates = useCallback(() => {
    // Immediately execute all pending updates
    pendingUpdatesRef.current.forEach((update) => {
      clearTimeout(update.timeoutId);
      updateContact({
        id: update.contactId,
        properties: Object.keys(update.properties).length > 0 ? (update.properties as ContactPropertiesInput) : undefined,
        customProperties: Object.keys(update.customProperties || {}).length > 0 ? update.customProperties : undefined,
      }).catch((error) => {
        console.error('Failed to flush pending update:', error);
      });
    });
    pendingUpdatesRef.current.clear();
  }, [updateContact]);

  return {
    scheduleUpdate,
    flushPendingUpdates,
    hasPendingUpdates: pendingUpdatesRef.current.size > 0,
  };
};
